import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PhoneCountryInput from '../common/PhoneCountryInput';
import { logout } from '../../features/auth/authSlice';
import {
  assignOrganizationUserRole,
  deleteOrganizationUserAccount,
  getAccountDeleteErrorMessage,
  inviteOrganizationUser,
  listOrganizationRoles,
  listOrganizationUsers,
  removeOrganizationUser,
  setOrganizationUserSuspended,
} from '../../services/organizationUserService';

const OrganizationUsersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth?.user);
  const isOrgAdmin = Boolean(
    authUser?.is_organization_admin ?? authUser?.is_organization_owner
  );
  const currentUserId = authUser?.id ?? authUser?.user_id ?? null;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [assigningRoleForUserId, setAssigningRoleForUserId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    phone: '',
    roleId: '',
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listOrganizationUsers();
      const list = Array.isArray(data?.members)
        ? data.members
        : Array.isArray(data?.users)
          ? data.users
        : Array.isArray(data)
          ? data
          : [];
      setUsers(list);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not load organization users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadRoles = useCallback(async () => {
    try {
      const data = await listOrganizationRoles();
      const list = Array.isArray(data?.roles)
        ? data.roles
        : Array.isArray(data)
          ? data
          : [];
      setRoles(list);
    } catch {
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const roleOptions = useMemo(
    () => roles.map((r) => ({ id: String(r.id), name: r.name, isDefault: Boolean(r.is_default) })),
    [roles]
  );

  useEffect(() => {
    if (!showInvite) return;
    const contributor = roleOptions.find((r) => r.isDefault);
    setInviteForm((prev) => ({
      ...prev,
      roleId: prev.roleId || contributor?.id || '',
    }));
  }, [showInvite, roleOptions]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.roleId) {
      setError('Role is required.');
      return;
    }
    setInviting(true);
    setError('');
    setSuccess('');
    try {
      const result = await inviteOrganizationUser({
        name: inviteForm.name.trim(),
        email: inviteForm.email.trim(),
        phone: inviteForm.phone.trim(),
        roleId: inviteForm.roleId,
      });
      setSuccess(result?.message || 'Invitation sent successfully.');
      setShowInvite(false);
      setInviteForm({ name: '', email: '', phone: '', roleId: '' });
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not send invitation.');
    } finally {
      setInviting(false);
    }
  };

  const handleAssignRole = async (user, roleId) => {
    const userId = user.id ?? user.user_id;
    if (!userId || !roleId) return;
    setAssigningRoleForUserId(userId);
    setError('');
    setSuccess('');
    try {
      const result = await assignOrganizationUserRole(userId, roleId);
      setSuccess(result?.message || 'User role updated successfully.');
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not assign role.');
    } finally {
      setAssigningRoleForUserId(null);
    }
  };

  const toggleSuspended = async (user) => {
    const userId = user.id ?? user.user_id;
    const suspended = !(user.suspended ?? user.is_suspended ?? false);
    setBusyUserId(userId);
    setError('');
    setSuccess('');
    try {
      const result = await setOrganizationUserSuspended(userId, suspended);
      setSuccess(result?.message || (suspended ? 'User suspended.' : 'User reactivated.'));
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not update user status.');
    } finally {
      setBusyUserId(null);
    }
  };

  const removeUser = async (user) => {
    const userId = user.id ?? user.user_id;
    const label = user.name || user.email || 'this user';
    if (!window.confirm(`Remove ${label} from this organization? Their login will remain; only membership ends.`)) {
      return;
    }
    setBusyUserId(userId);
    setError('');
    setSuccess('');
    try {
      const result = await removeOrganizationUser(userId);
      setSuccess(result?.message || 'User removed from organization.');
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not remove user.');
    } finally {
      setBusyUserId(null);
    }
  };

  const deleteUserAccount = async (user) => {
    const userId = user.id ?? user.user_id;
    const label = user.name || user.email || 'this user';
    const isSelf = currentUserId != null && String(userId) === String(currentUserId);
    const confirmMsg = isSelf
      ? 'Permanently delete your account? You will be signed out and cannot undo this.'
      : `Permanently delete ${label}'s account? This removes their login (not just org membership).`;
    if (!window.confirm(confirmMsg)) return;

    setBusyUserId(userId);
    setError('');
    setSuccess('');
    try {
      const result = await deleteOrganizationUserAccount(userId);
      if (isSelf) {
        try {
          await dispatch(logout()).unwrap();
        } catch {
          // Tokens already revoked server-side
        }
        navigate('/login', { replace: true });
        return;
      }
      setSuccess(result?.message || 'User account deleted.');
      await loadUsers();
    } catch (err) {
      setError(getAccountDeleteErrorMessage(err));
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="organization-users-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="fw-semibold mb-1">Organization users</h4>
          <p className="text-muted mb-0">
            Invite, suspend, remove from org, or delete member accounts.
          </p>
        </div>
        {isOrgAdmin && (
          <Button variant="primary" onClick={() => setShowInvite(true)}>
            <i className="ri-user-add-line me-2" />
            Invite user
          </Button>
        )}
      </div>

      {!isOrgAdmin && (
        <Alert variant="warning">
          Only organization admins can manage users.
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th className="ps-3">Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="text-end pe-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      No users found in this organization.
                    </td>
                  </tr>
                )}
                {users.map((user) => {
                  const userId = user.id ?? user.user_id;
                  const suspended = user.suspended ?? user.is_suspended ?? false;
                  return (
                    <tr key={userId}>
                      <td className="ps-3 fw-medium">{user.name || '—'}</td>
                      <td>{user.email || '—'}</td>
                      <td>
                        {isOrgAdmin ? (
                          <Form.Select
                            size="sm"
                            value={String(user.role_id ?? user.role?.id ?? '')}
                            disabled={assigningRoleForUserId === userId || roleOptions.length === 0}
                            onChange={(e) => handleAssignRole(user, e.target.value)}
                            style={{ minWidth: '180px' }}
                          >
                            <option value="">
                              {roleOptions.find((r) => r.isDefault)?.name || 'Contributor (default)'}
                            </option>
                            {roleOptions.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                                {role.isDefault ? ' (default)' : ''}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          user.role_name ?? user.role?.name ?? roleOptions.find((r) => r.isDefault)?.name ?? 'Contributor'
                        )}
                      </td>
                      <td>
                        {suspended ? (
                          <Badge bg="warning">Suspended</Badge>
                        ) : (
                          <Badge bg="success">Active</Badge>
                        )}
                      </td>
                      <td className="text-end pe-3">
                        {isOrgAdmin ? (
                          <div className="d-inline-flex flex-wrap justify-content-end gap-2">
                            <Button
                              size="sm"
                              variant={suspended ? 'outline-success' : 'outline-warning'}
                              disabled={busyUserId === userId}
                              onClick={() => toggleSuspended(user)}
                            >
                              {suspended ? 'Reactivate' : 'Suspend'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={busyUserId === userId}
                              title="Ends organization membership only"
                              onClick={() => removeUser(user)}
                            >
                              Remove from org
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={busyUserId === userId}
                              title="Soft-deletes the user account"
                              onClick={() => deleteUserAccount(user)}
                            >
                              {currentUserId != null && String(userId) === String(currentUserId)
                                ? 'Delete my account'
                                : 'Delete account'}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted small">No permission</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showInvite} onHide={() => setShowInvite(false)} centered>
        <Form onSubmit={handleInvite}>
          <Modal.Header closeButton>
            <Modal.Title>Invite user</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small">
              The user receives a temporary password by email and must change it on first login.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={inviteForm.name}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Phone <span className="text-danger">*</span>
              </Form.Label>
              <PhoneCountryInput
                id="invite-user-phone"
                value={inviteForm.phone}
                className="bg-light border-light"
                placeholder="Enter phone number"
                required
                onChange={(phone) => setInviteForm((prev) => ({ ...prev, phone }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={inviteForm.roleId}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, roleId: e.target.value }))}
                required
                disabled={roleOptions.length === 0}
              >
                <option value="">Select a role</option>
                {roleOptions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                    {role.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </Form.Select>
              {roleOptions.length === 0 && (
                <div className="text-muted small mt-1">
                  No roles found for this organization.
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowInvite(false)} disabled={inviting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={inviting}>
              {inviting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Sending...
                </>
              ) : (
                'Send invite'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default OrganizationUsersPage;
